import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ScoreGrade, scoreColor, SentenceScoreStatus, sentenceScoreStatus, sentenceScoreStatusMessage } from '../Score';
import { AudioApi } from '../api/AudioApi';
import { CommentApi } from '../api/CommentApi';
import ShareIcon from '../asset/icon-share-black.png';
import SpeakerBlueIcon from '../asset/icon-speaker-blue.svg';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useSentenceScoreDialog } from '../context/SentenceScoreDialogContext';
import { useKaiTiFontStyles } from '../font/KaiTiFont';
import ShareDialog from './ShareDialog'; // Adjust path as needed
import { TeacherAudioActions, getSelectedTeacherSpeakerRole } from './TeacherAudioReportButton';

import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // outline rounded doesn't exist?
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import Paper from '@mui/material/Paper';
import Draggable from 'react-draggable';

const SentenceScoreDialog = () => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const {
        isOpen,
        sentenceScore,
        learnWithCharacter,
        closeDialog,
        isShareOpen,
        openShareDialog,
        closeShareDialog,
    } = useSentenceScoreDialog();

    const kaiTiFontClass = useKaiTiFontStyles();

    const [position, setPosition] = React.useState({ x: 100, y: 100 });
    const [compactMode, setCompactMode] = React.useState(false);
    const [isReportDialogOpen, setIsReportDialogOpen] = React.useState(false);
    const [reportCategory, setReportCategory] = React.useState('grading');
    const [reportText, setReportText] = React.useState('');
    const [includeAudioAndMetadata, setIncludeAudioAndMetadata] = React.useState(true);
    const [isSubmittingReport, setIsSubmittingReport] = React.useState(false);


    const tipArray = ['1. Check your audio for background noise or clipping.',
        '2. Compare your spoken pinyin to the reference pinyin',
        '3. Compare your audio to the reference audio'];

    const [tipIndex, setTipIndex] = React.useState(-1);
    const [previousSentenceId, setPreviousSentenceId] = React.useState(-1);
    const [incorrectCount, setIncorrectCount] = React.useState(0);

    React.useEffect(() => {
        if (!sentenceScore) {
            return;
        }
        if (sentenceScore.sentence_id == previousSentenceId) {
            if (sentenceScore.passing_grade == ScoreGrade.Good) {
                setIncorrectCount(0)
            } else {
                setIncorrectCount(prev => prev + 1)
            }
        } else {
            setPreviousSentenceId(sentenceScore.sentence_id);
            if (sentenceScore.passing_grade == ScoreGrade.Good) {
                setIncorrectCount(0)
            } else {
                setIncorrectCount(1)
            }
        }
    }, [sentenceScore, previousSentenceId])

    React.useEffect(() => {
        if (!sentenceScore) {
            return;
        }
        if (incorrectCount > 1) {
            setTipIndex(prev => (prev + 1) % tipArray.length)
        }
    }, [incorrectCount, sentenceScore, tipArray.length])

    const PaperComponent = React.useCallback((props: any) => {
        return (
            <Draggable
                handle="#draggable-dialog-title"
                cancel={'[class*="MuiDialogContent-root"]'}
                position={position}
                bounds="body"
                onStop={(_e, data) => {

                    const newX = data.x;
                    const newY = data.y;

                    // When dragged into the top area (e.g., over a nav bar)
                    if (data.y < 48 && !compactMode) {
                        setCompactMode(true);
                    } else if (data.y >= 48 && compactMode) {
                        setCompactMode(false);
                    }
                    setPosition({ x: newX, y: newY });
                }}

            >
                <Paper {...props} />
            </Draggable>
        );
    }, [compactMode, position]);

    React.useLayoutEffect(() => {
        const dialogWidth = 600;
        const dialogHeight = 400;

        const x = window.innerWidth / 2 - dialogWidth / 2;
        const y = window.innerHeight / 2 - dialogHeight / 2;

        setPosition({ x, y });
    }, []);

    if (!sentenceScore) {
        return null;
    }

    const handleSpeakerClick = (sentenceId: number) => {
        const audioPromise = AudioApi.getTeacherSentence(sentenceId)

        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob)
                // create a new Audio object and set the source to the blob URL
                // const audio = new Audio(URL.createObjectURL(blob));
                // audio.play();
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    const handleSubmitReport = () => {
        const trimmedReportText = reportText.trim();
        if (!trimmedReportText || isSubmittingReport) {
            return;
        }

        const scoreLength = Math.max(
            sentenceScore.score_grade?.length ?? 0,
            sentenceScore.pinyin_list_transcript?.length ?? 0,
            sentenceScore.pinyin_list_spoken?.length ?? 0,
            1
        );
        const metadataText = includeAudioAndMetadata
            ? [
                '',
                '---',
                `Sentence: ${sentenceText || 'Unavailable'}`,
                `Heard: ${spokenPinyinText}`,
                `Target: ${targetPinyinText}`,
                `Sentence score id: ${sentenceScore.id}`,
                `Sentence id: ${sentenceScore.sentence_id}`,
            ].join('\n')
            : '';

        setIsSubmittingReport(true);
        CommentApi.createSentenceScoreComment({
            id: 0,
            bug_report: `${trimmedReportText}${metadataText}`,
            bug_category: reportCategory,
            sentence_score_id: sentenceScore.id,
            sentence_score_bug_flag_list: Array(scoreLength).fill(true),
        })
            .then(response => {
                if (response.status >= 300) {
                    throw new Error('Unable to submit sentence score report: ' + response.status);
                }
                setErrorMessage('Thanks - report submitted.');
                setIsReportDialogOpen(false);
                setReportCategory('grading');
                setReportText('');
                setIncludeAudioAndMetadata(true);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setIsSubmittingReport(false);
            });
    };

    const scoreStatus = sentenceScoreStatus(sentenceScore);
    const scoreStatusMessage = sentenceScoreStatusMessage(sentenceScore);
    const hidePinyinScore = scoreStatus == SentenceScoreStatus.NoSpeech;
    const spokenPinyinText = hidePinyinScore
        ? scoreStatusMessage || ''
        : sentenceScore.pinyin_list_spoken.join(' ');
    const targetPinyinText = sentenceScore.pinyin_list_transcript.join(' ');
    const sentenceText = sentenceScore.text.replace(/-/g, '');

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {  // do not close when clicking off of dialog
                        closeDialog();
                    }
                }}
                hideBackdrop
                disableEnforceFocus
                disableEscapeKeyDown={false}
                PaperComponent={PaperComponent}
                PaperProps={{
                    sx: {
                        backgroundColor: 'background.default',
                        border: '2px solid #ddd',
                        boxShadow: 6,
                        borderRadius: 2,
                        maxWidth: compactMode ? '90vw' : '600px',  // 💡 key line
                        minWidth: 0,
                        width: 'fit-content',                      // 👈 allow width to grow with content
                        m: compactMode ? 0.5 : 1, // <--- margin around the dialog
                        top: 0,               // ⬅️ override top/left/transform
                        left: 0,
                        position: 'absolute',
                        transform: 'none',
                    }
                }}
                sx={{
                    pointerEvents: 'none',
                    '& .MuiPaper-root': {
                        pointerEvents: 'auto'
                    }
                }}
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title" sx={{ px: compactMode ? 1 : 3, py: compactMode ? 0.5 : 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <DragIndicatorRoundedIcon fontSize="small" />
                        <Typography variant={compactMode ? "h6" : "h4"}>Spoken Accuracy</Typography>
                        {compactMode && incorrectCount > 1 && tipIndex >= 0 && (
                            <Typography sx={{ mt: 3, display: 'flex', alignItems: 'center' }} variant="h6">
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#0062FF', mr: 1, mb: 0.5 }} />
                                {tipArray[tipIndex]}
                            </Typography>
                        )}

                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ px: compactMode ? 1 : 3, py: compactMode ? 1 : 2 }}>
                    <Stack direction={compactMode ? "row" : "column"} alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
                        {/* Grade Key */}
                        {compactMode == false && (
                            <Stack direction="row" className="border-light" sx={{ p: 2, mb: 2 }}>
                                <Typography sx={{
                                    backgroundColor: `${scoreColor(ScoreGrade.Good)}22`,
                                    px: 1, mx: 1, borderRadius: 2
                                }}>
                                    Wonderful
                                </Typography>
                                <Typography sx={{
                                    backgroundColor: `${scoreColor(ScoreGrade.OK)}22`,
                                    px: 1, mx: 1, borderRadius: 2
                                }}>
                                    So-So
                                </Typography>
                                <Typography sx={{
                                    backgroundColor: `${scoreColor(ScoreGrade.Bad)}22`,
                                    px: 1, mx: 1, borderRadius: 2
                                }}>
                                    Try Again
                                </Typography>
                            </Stack>
                        )}

                        {/* Spoken result */}
                        <Stack direction="row" alignItems="center">
                            {compactMode == false && (
                                <Typography sx={{ mx: 1 }} variant="h5">
                                    The system heard ...
                                </Typography>
                            )}
                            {sentenceScore.audioData && (
                                <IconButton onClick={() => playAudio(sentenceScore.audioData!)}>
                                    <img src={SpeakerBlueIcon} style={{ width: '24px', height: '24px' }} />
                                </IconButton>
                            )}
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                            {scoreStatusMessage && (
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        color: scoreStatus == SentenceScoreStatus.TargetMismatch ? '#A33A00' : '#5A4A00',
                                        mr: 1,
                                    }}
                                >
                                    {scoreStatusMessage}
                                </Typography>
                            )}
                            {!hidePinyinScore && (sentenceScore.pinyin_list_spoken.map((pinyinElement, index) => (<>
                                {(pinyinElement !== '-' || sentenceScore.text[index] !== '-') && (
                                    <Stack key={index} alignItems="center" justifyContent="center"
                                        sx={{
                                            backgroundColor: `${scoreColor(sentenceScore.score_grade[index])}22`,
                                            borderRadius: 2, px: 1
                                        }}>
                                        <Typography>{pinyinElement || '-'}</Typography>
                                        {learnWithCharacter && (
                                            <Typography className={kaiTiFontClass.customFont} sx={{ fontSize: '24px' }}>
                                                {sentenceScore.text[index]}
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </>)))}
                        </Stack>

                        {/* Target Sentence */}
                        <Stack direction="row" alignItems="center" mt={compactMode ? 0 : 3}>
                            {compactMode == false && (
                                <Typography sx={{ mx: 1 }} variant="h5">
                                    Please try this ...
                                </Typography>
                            )}
                            <TeacherAudioActions
                                context={{
                                    sentenceId: sentenceScore.sentence_id,
                                    textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
                                    audioText: sentenceText,
                                    audioRole: 'sentence_score_reference',
                                }}
                                onPlay={() => handleSpeakerClick(sentenceScore.sentence_id)}
                                speakerSize={24}
                            />
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                            {sentenceScore.pinyin_list_transcript.map((pinyinElement, index) => (<>
                                {(pinyinElement !== '-' || sentenceScore.text[index] != '-') && (
                                    <Stack key={index} alignItems="center" justifyContent="center"
                                        sx={{
                                            backgroundColor: `${scoreColor(ScoreGrade.Good)}22`,
                                            borderRadius: 2, px: 1
                                        }}>
                                        <Typography>{pinyinElement}</Typography>
                                        {learnWithCharacter && (
                                            <Typography className={kaiTiFontClass.customFont} sx={{ fontSize: '24px' }}>
                                                {sentenceScore.text[index]}
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </>))}
                        </Stack>
                        {compactMode && (
                            <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                                <Tooltip title="Report a problem">
                                    <IconButton aria-label="Report a problem" onClick={() => setIsReportDialogOpen(true)}>
                                        <ReportProblemRoundedIcon sx={{ color: '#0062FF' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Share">
                                    <IconButton onClick={openShareDialog}>
                                        <img src={ShareIcon} alt="Share" style={{ width: '16px', height: '16px' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Close">
                                    <IconButton onClick={closeDialog}>
                                        <CloseRoundedIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        )}
                        {compactMode == false && incorrectCount > 1 && tipIndex >= 0 && (
                            <Typography sx={{ mt: 3, display: 'flex', alignItems: 'center' }} variant="h6">
                                <InfoOutlinedIcon sx={{ fontSize: 24, color: '#0062FF', mr: 1, mb: 0.5 }} />
                                {tipArray[tipIndex]}
                            </Typography>
                        )}

                    </Stack>

                </DialogContent>

                {compactMode == false && (
                    <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                        <Button
                            onClick={() => setIsReportDialogOpen(true)}
                            variant="outlined"
                            startIcon={<ReportProblemRoundedIcon />}
                        >
                            Report
                        </Button>
                        <Button
                            onClick={openShareDialog}
                            variant="contained"
                            className="secondaryButton"
                        >
                            Share
                        </Button>
                        <Button onClick={closeDialog} variant="contained">Close</Button>
                    </DialogActions>
                )}
            </Dialog >

            {/* Nested Share Dialog */}
            {
                sentenceScore.sentence_id && (
                    <ShareDialog
                        isOpen={isShareOpen}
                        onClose={closeShareDialog}
                        sentenceScoreId={sentenceScore.id}
                    />
                )
            }
            <Dialog
                open={isReportDialogOpen}
                onClose={() => setIsReportDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Report a Problem</DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Spoken accuracy details:
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Stack spacing={0.5}>
                                <Typography variant="body2">Sentence: {sentenceText || 'Unavailable'}</Typography>
                                <Typography variant="body2">Heard: {spokenPinyinText}</Typography>
                                <Typography variant="body2">Target: {targetPinyinText}</Typography>
                            </Stack>
                        </Paper>

                        <FormControl fullWidth size="small">
                            <InputLabel id="sentence-score-report-category-label">Category</InputLabel>
                            <Select
                                labelId="sentence-score-report-category-label"
                                value={reportCategory}
                                label="Category"
                                onChange={(e) => setReportCategory(String(e.target.value))}
                            >
                                <MenuItem value="grading">Grading seems wrong</MenuItem>
                                <MenuItem value="transcript">Transcript/pinyin is wrong</MenuItem>
                                <MenuItem value="audio">Audio issue</MenuItem>
                                <MenuItem value="content">Content issue</MenuItem>
                                <MenuItem value="bug">Bug / UI problem</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label="What went wrong?"
                            value={reportText}
                            onChange={(e) => setReportText(e.target.value)}
                            multiline
                            minRows={4}
                            fullWidth
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeAudioAndMetadata}
                                    onChange={(e) => setIncludeAudioAndMetadata(e.target.checked)}
                                />
                            }
                            label="Include audio clip and scoring metadata"
                        />

                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Score available: {String(Boolean(sentenceScore.score_grade))} / Audio available: {String(Boolean(sentenceScore.audioData))}
                        </Typography>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setIsReportDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitReport}
                        disabled={String(reportText).trim().length === 0 || isSubmittingReport}
                    >
                        {isSubmittingReport ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SentenceScoreDialog;
